/**
 * Category mapping configuration
 * Maps product categories to unified display categories
 * This allows merging categories like "carafe" into "Glassware Bar"
 */

export interface CategoryMapping {
  [originalCategory: string]: string; // Maps original category to unified category
}

// Default category mappings - can be updated by user
export const categoryMappings: CategoryMapping = {
  'hiball': 'Drinkware',
  'hiball glass': 'Drinkware',
  'Hiball': 'Drinkware',
  'Hiball Glass': 'Drinkware',
  'tumbler': 'Drinkware',
  'Tumbler': 'Drinkware',
  'TUMBLER': 'Drinkware',
  'tumblers': 'Drinkware',
  'Tumblers': 'Drinkware',
  'TUMBLERS': 'Drinkware',
  'saucer champagne': 'Champagne Glass',
  'saucer champagne glass': 'Champagne Glass',
  'champagne flutes': 'Champagne Glass',
  'Saucer Champagne': 'Champagne Glass',
  'Saucer Champagne Glass': 'Champagne Glass',
  'Champagne Flutes': 'Champagne Glass',
  'bell': 'Other',
  'BELL': 'Other',
  'Bell': 'Other',
  // Example: 'carafe': 'Glassware Bar',
  // Add more mappings as needed
};

/**
 * Get the mapped category for a product
 * If no mapping exists, returns the original category
 */
export function getMappedCategory(
  originalCategory: string | undefined,
  productType: string | undefined
): string {
  if (!originalCategory && !productType) return 'Other';
  
  // Check category first
  if (originalCategory) {
    const lowerCategory = originalCategory.toLowerCase();
    // Check direct mapping (case-insensitive)
    if (categoryMappings[lowerCategory]) {
      return categoryMappings[lowerCategory];
    }
    // Check exact match
    if (categoryMappings[originalCategory]) {
      return categoryMappings[originalCategory];
    }
  }
  
  // Check product type
  if (productType) {
    const lowerType = productType.toLowerCase();
    // Check direct mapping (case-insensitive)
    if (categoryMappings[lowerType]) {
      return categoryMappings[lowerType];
    }
    // Check exact match
    if (categoryMappings[productType]) {
      return categoryMappings[productType];
    }
  }
  
  // Return original category or type, prioritizing category
  return originalCategory || productType || 'Other';
}

/**
 * Update category mapping
 * Call this function to merge categories (e.g., merge "carafe" into "Glassware Bar")
 */
export function updateCategoryMapping(fromCategory: string, toCategory: string): void {
  categoryMappings[fromCategory.toLowerCase()] = toCategory;
}

/**
 * Get all unified categories from both collections
 * Applies category mappings to merge categories
 */
export function getUnifiedCategories(
  trophiesCategories: string[],
  galwayCategories: string[]
): string[] {
  const allCategories = new Set<string>();
  
  // Helper function to get mapped category
  const getMapped = (cat: string): string => {
    const lower = cat.toLowerCase();
    return categoryMappings[lower] || cat;
  };
  
  // Add all categories from both collections, applying mappings
  trophiesCategories.forEach(cat => {
    if (cat !== 'All') {
      const mapped = getMapped(cat);
      allCategories.add(mapped);
    }
  });
  
  galwayCategories.forEach(cat => {
    if (cat !== 'All') {
      const mapped = getMapped(cat);
      allCategories.add(mapped);
    }
  });
  
  // Convert to array and sort
  const sortedCategories = Array.from(allCategories).sort();
  
  // Put "All" first
  return ['All', ...sortedCategories];
}

